import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles.scss";
import AppHeader from "../../components/header";
import AppConfig from "../../helpers/config";
import Slider from "react-slick";
import GoogleMapReact from "google-map-react";
// import HorizontalScroll from 'react-scroll-horizontal';
import {
    getAppConfig,
    getHotels,
    setActivityIndicator,
    setResponseType,
    setStatusCodeByGuid,
} from "../../redux/slices/appconfigSlice";
import PricePin from "../../assets/images/offer-map-pin.png";
import HoverPin from "../../assets/images/search-icon.svg";
import GeniusIcon from "../../assets/images/genius_bestemming.svg";
import GeniusImage from "../../assets/images/genius-bird.png";
import useWindowDimensions from "../../utils/getWindowDimensions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTranslationCustom from "../../i18n/customHook/useTranslation";
import { useDispatch, useSelector } from "react-redux";
import {
    setPriceRangeValue,
    getSearchFilters,
} from "../../redux/slices/searchFilterSlice";
import { faAngleLeft, faAngleRight, faArrowLeftLong, faArrowRightLong, faLayerGroup, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
    getAnywhereSearchExpediaUrl,
    getCurrencyBadge,
    getSearchOnlyListingParams,
    navigateToDetailPage,
    openExternalLink,
} from "../../helpers/common";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import HotelItemMap from "../../components/HotelItem/HotelItemMap";
import HotelItemDetailMap from "../../components/HotelItem/HotelItemDetailMap";
import Lottie from "lottie-react";
import GeniusJson from "../../assets/images/lottieViews/genius.json";
import BulbJson from "../../assets/images/lottieViews/bulb.json";
import { toast } from "react-toastify";
import { isTablet } from "react-device-detect";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

// Street-level zoom used when a slider swipe flies to a marker; close enough to
// separate hotels that overlap at city-level zoom.
const SLIDER_MARKER_ZOOM = 17;
// Pins (50px wide) whose anchors land closer than this on screen hide each
// other's price, so they're counted as one stack.
const MARKER_STACK_DISTANCE_PX = 30;
// Zoom cap when a stack badge is tapped: hotels sharing an address never
// separate, so don't fly all the way to the map's max zoom for them.
const MARKER_STACK_MAX_ZOOM = 19;

// Puts a count badge on the topmost pin of every group of overlapping pins so
// users can tell several hotels are stacked under one marker.
function applyMarkerStackBadges(overlays, onStackClick) {
    const drawn = [...overlays.values()].filter((overlay) => overlay.div?.parentNode && overlay.point);
    if (!drawn.length) return;

    const domOrder = [...drawn[0].div.parentNode.children];
    const zIndexOf = (overlay) => Number(overlay.div.style.zIndex) || 0;
    // Topmost first: the selected pin (lifted via z-index), then later DOM siblings.
    drawn.sort((a, b) => zIndexOf(b) - zIndexOf(a) || domOrder.indexOf(b.div) - domOrder.indexOf(a.div));

    const claimed = new Set();
    drawn.forEach((top) => {
        if (claimed.has(top)) return;
        const stack = drawn.filter((overlay) =>
            !claimed.has(overlay) &&
            Math.abs(overlay.point.x - top.point.x) < MARKER_STACK_DISTANCE_PX &&
            Math.abs(overlay.point.y - top.point.y) < MARKER_STACK_DISTANCE_PX
        );
        stack.forEach((overlay) => {
            claimed.add(overlay);
            const isTop = overlay === top;
            // The top pin is drawn as a pile standing in for the pins under it.
            overlay.div.classList.toggle("is-stacked-under", !isTop);
            setMarkerStackBadge(overlay.div, isTop && stack.length > 1 ? stack : null, onStackClick);
        });
    });
}

const [layerIconWidth, layerIconHeight, , , layerIconPath] = faLayerGroup.icon;
const MARKER_STACK_ICON_SVG =
    `<svg viewBox="0 0 ${layerIconWidth} ${layerIconHeight}" aria-hidden="true"><path fill="currentColor" d="${layerIconPath}"/></svg>`;

function setMarkerStackBadge(markerDiv, stack, onStackClick) {
    let badge = markerDiv.querySelector(".marker-stack-badge");
    markerDiv.classList.toggle("is-stack-top", Boolean(stack));
    if (!stack) {
        badge?.remove();
        markerDiv.querySelectorAll(".marker-stack-ghost").forEach((ghost) => ghost.remove());
        return;
    }
    if (!badge) {
        // Faded bubbles peeking out behind the pin; prepended so the pin
        // itself paints over them.
        [2, 1].forEach((depth) => {
            const ghost = document.createElement("span");
            ghost.className = `marker-stack-ghost marker-stack-ghost-${depth}`;
            ghost.style.backgroundImage = `url(${PricePin})`;
            markerDiv.prepend(ghost);
        });

        badge = document.createElement("div");
        badge.className = "marker-stack-badge";
        badge.innerHTML = `${MARKER_STACK_ICON_SVG}<span></span>`;
        badge.addEventListener("click", (event) => {
            // Keep the pin's own click from selecting the top offer.
            event.stopPropagation();
            onStackClick(badge.stackPositions);
        });
        markerDiv.appendChild(badge);
    }
    badge.querySelector("span").textContent = String(stack.length);
    badge.stackPositions = stack.map((overlay) => overlay.position);
}

const MapOffersPage = () => {
    const dispatch = useDispatch();
    // guid: search correlation ID, page: pagination page, prevRoute: previous route context (for future back-navigation)
    const { guid, prevRoute, page: pageParam } = useParams();
    const scrollRef = useRef(null);
    const offerRefs = useRef([]); // array of refs for offers
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const slider = useRef(null);
    const mapChangeTimerRef = useRef(null);
    const lastMapCenterRef = useRef({ lat: null, lng: null });
    const lastCityAreaRef = useRef("");
    const geocodeRequestSeqRef = useRef(0);
    const programmaticMovementSeqRef = useRef(0);
    const hasAnimatedForCurrentLocationsRef = useRef(false);
    const previousLocationsKeyRef = useRef("");
    const userHasInteractedRef = useRef(false);
    const overlaysRef = useRef(new Map());
    const stackBadgeFrameRef = useRef(null);
    const stackBadgeClickRef = useRef(null);
    const bottomOffersRef = useRef(null);
    const isFirstRun = useRef(true);
    const skipNextPageEffect = useRef(false);
    const isAnimatingRef = useRef(false);
    const searchTypeRef = useRef("Map");
    // Tracks whether the in-flight search is a coordinates-based ("find in area")
    // search. Held in a ref so it survives the async getHotels round-trip, since
    // the searchCoordinates state may be cleared before the animation effect runs.
    const isCoordinatesSearchRef = useRef(false);
    const { width } = useWindowDimensions();
    const appConfig = useSelector(getAppConfig);
    // appconfig.responseType is persisted, so a "RealTime" value can still be sitting
    // in localStorage from a session that ended before its retry dialog ran. Snapshot
    // whatever it was the instant this page mounted so the RealTime effect below can
    // tell "leftover from before I existed" apart from "a search I just triggered".
    const staleResponseTypeAtMountRef = useRef(appConfig.responseType === "RealTime");
    const { pagination } = appConfig;
    const visited = (pagination?.currentPage - 1) * pagination?.pageSize || 0;
    const remaining =
        Math.max(
            pagination?.totalItems - pagination?.currentPage * pagination?.pageSize,
            0
        ) || 0;
    // react-slick only re-measures on resize or a change in child count, so a new
    // offer set must remount it — otherwise it keeps the previous set's geometry.
    const offersKey = useMemo(
        () => (appConfig.offers || []).map((offer) => offer?.id).join("|"),
        [appConfig.offers]
    );
    const loginConfig = useSelector(getLoginConfig);
    const searchfilters = useSelector(getSearchFilters);
    // const selectedDesireCountry = useSelector(
    //     (state) => state.searchfilters.selectedDesireCountry
    // );

    const [page, setPage] = useState(Number(pageParam) || 1);
    // const [searchTrigger, setSearchTrigger] = useState(0);
    const [triggerNewSearch, setTriggerNewSearch] = useState(false);

    const hideGreenButtonRef = useRef(searchfilters?.selectedDesireCountry?.airportCode === "001" || searchfilters?.selectedDesireCountry?.id === "00001");
    const [hotelLocations, setLocations] = useState([]);
    const [mapInstance, setMapInstance] = useState(null);
    const [mapsApi, setMapsApi] = useState(null);
    const [switchToDetailSideBar, setSwitchToDetailSideBar] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [cityArea, setCityArea] = useState('');
    const [searchCoordinates, setSearchCoordinates] = useState('');
    const [geniusDestinationObject, setGeniusDestinationObject] = useState(null);
    const [performPriceExpandedSearch, setPerformPriceExpandedSearch] = useState(false);
    const { T } = useTranslationCustom();

    // Memoize defaultProps to prevent GoogleMap warning about changing default props
    const defaultProps = useMemo(() => ({
        center: {
            lat: Number(appConfig.offers?.[0]?.location?.geoLocation?.latitude) || 52.3676,
            lng: Number(appConfig.offers?.[0]?.location?.geoLocation?.longitude) || 4.9041,
        }, // Default center (fallback to Amsterdam)
        zoom: 14,
    }), []); // Empty dependency array - only calculate once on mount

    useEffect(() => {
        if (!mapInstance || !mapsApi || !hotelLocations?.length) {
            return;
        }

        const locationsKey = hotelLocations
            .map((loc) => `${loc?.id}-${Number(loc?.lat)}-${Number(loc?.lng)}`)
            .join("|");

        // Check if locations have changed
        const locationsChanged = locationsKey !== previousLocationsKeyRef.current;

        if (locationsChanged) {
            previousLocationsKeyRef.current = locationsKey;
            hasAnimatedForCurrentLocationsRef.current = false;
            userHasInteractedRef.current = false; // Reset user interaction on new locations
            resetMapAreaState(true);
        }

        // Only auto-fit if user hasn't manually interacted with the map
        if (locationsChanged && !userHasInteractedRef.current) {
            // Filter valid locations
            const validLocations = hotelLocations.filter((loc) => {
                const lat = Number(loc?.lat);
                const lng = Number(loc?.lng);
                return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            });

            // Check if animation should be triggered.
            // Skip the animation for coordinates-based ("find in area") searches.
            const isCoordinatesBasedSearch = isCoordinatesSearchRef.current;
            const shouldAnimate = !isCoordinatesBasedSearch &&
                (page !== 1 ||
                    (searchfilters?.selectedDesireCountry?.id === "00001" &&
                        searchfilters?.selectedDesireCountry?.airportCode === "001"));

            // console.log('shouldAnimate:', shouldAnimate, 'validLocations.length:', validLocations.length, 'page:', page, 'country:', searchfilters?.selectedDesireCountry);

            if (!hasAnimatedForCurrentLocationsRef.current && shouldAnimate && validLocations.length >= 3) {
                hasAnimatedForCurrentLocationsRef.current = true;
                animateClusterZoom(mapInstance, mapsApi, validLocations);
            } else {
                fitBoundsToMarkers(mapInstance, mapsApi, hotelLocations);
            }
        }

        scrollToOffer(0);
    }, [hotelLocations, mapInstance, mapsApi, searchCoordinates, searchfilters]);

    useEffect(() => {
        if (!mapInstance || !mapsApi) return undefined;

        // Track user interactions (drag, zoom)
        const dragStartListener = mapsApi.event.addListener(mapInstance, "dragstart", () => {
            userHasInteractedRef.current = true;
        });

        const zoomChangedListener = mapsApi.event.addListener(mapInstance, "zoom_changed", () => {
            // Ignore programmatic zoom changes during animation so auto-fit logic isn't broken
            if (!isAnimatingRef.current) {
                userHasInteractedRef.current = true;
            }
        });

        const idleListener = mapsApi.event.addListener(mapInstance, "idle", () => {
            // Skip geocoder calls while the cluster zoom animation is running
            if (isAnimatingRef.current) return;
            const center = mapInstance.getCenter();
            if (!center) return;
            handleMapChange({ center: { lat: center.lat(), lng: center.lng() } });
        });

        return () => {
            mapsApi.event.removeListener(dragStartListener);
            mapsApi.event.removeListener(zoomChangedListener);
            mapsApi.event.removeListener(idleListener);
        };
    }, [mapInstance, mapsApi]);

    useEffect(() => {
        if (skipNextPageEffect.current) {
            skipNextPageEffect.current = false;
            return;
        }
        if (isFirstRun.current) {
            searchTypeRef.current = "Map";
        } else {
            searchTypeRef.current = "Pagination";
        }
        handleSearchFromScratch(page, guid, false, false, searchCoordinates);
    }, [page]);

    // useEffect(() => {
    //
    //     setPage(1);
    //     setSearchTrigger((t) => t + 1); // force the search even if page = 1
    // }, [searchfilters.defaultPrice]);

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false; // skip first run
            return;
        }
        if (!appConfig.offers || appConfig.offers.length === 0) {
            setLocations([]);
            return;
        }
        const _locations = appConfig.offers.map((f) => {
            const geoLocation = f?.location?.geoLocation;
            return {
                id: f.id,
                lat: geoLocation?.latitude,
                lng: geoLocation?.longitude,
                name: f?.name,
                perPersonPrice: f?.perPersonPrice,
                selected: f.id === appConfig.offers[0]?.id,
            };
        });

        setLocations(_locations);
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 0, behavior: "smooth" });
        }
    }, [appConfig.offers]);

    useEffect(() => {
        // A "RealTime" value already present at mount is leftover from a previous
        // session/guid (see staleResponseTypeAtMountRef above) — consume it silently
        // instead of navigating away or popping a warning dialog for a search this
        // page never made. A value that transitions to "RealTime" *while* this page
        // is mounted is always a real, freshly-triggered search and is unaffected.
        if (staleResponseTypeAtMountRef.current) {
            staleResponseTypeAtMountRef.current = false;
            if (appConfig.responseType === "RealTime") {
                dispatch(setResponseType(null));
            }
            return;
        }
        if (appConfig.responseType === "RealTime") {
            const runRealTimeSearch = async () => {
                navigate("/map/" + appConfig.correlationId + `/offers/${prevRoute}/1`, {
                    replace: true,
                });
                // setPage(1);
                dispatch(setResponseType(null));

                const handleRealTimeResponse = async () => {
                    const result = await Swal.fire({
                        title: T("Common:No_Matches_Under_Budget"),
                        text: appConfig.statusCodeByGuid?.warnings?.[0]?.description || T("Common:No_Matches_Under_Budget_Description"), // 'We couldn’t find any direct flights. Would you like to see results with a stopover?'
                        iconHtml: `
                            <img src=${GeniusImage}
                            alt="info"
                            style="width:80px;" />
                            `,
                        showCancelButton: true,
                        confirmButtonText: T("Buttons:Button_Yes"),
                        cancelButtonText: T("Buttons:Button_No"),
                        confirmButtonColor: "var(--green)",
                        cancelButtonColor: "#596C7F",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    // IF USER CLICKS YES → retry with full app price range
                    if (result.isConfirmed) {
                        dispatch(setPriceRangeValue({ low: AppConfig.minPrice, high: AppConfig.maxPrice }));
                        dispatch(setStatusCodeByGuid({}));
                        setPerformPriceExpandedSearch(true); // Trigger search after Redux updates
                        return;
                    }
                };

                const handleNoAvailabilityResponse = async () => {
                    await Swal.fire({
                        title: T("Common:Offers_Not_Found"),
                        text: appConfig.statusCodeByGuid?.warnings?.[0]?.description,
                        iconHtml: `
                            <img src=${GeniusImage}
                            alt="info"
                            style="width:80px;" />
                            `,
                        showCancelButton: false,
                        confirmButtonText: T("Buttons:Button_Ok"),
                        confirmButtonColor: "var(--green)",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                };

                if (appConfig.correlationId === appConfig.statusCodeByGuid?.guid && appConfig.statusCodeByGuid?.warnings?.[0]?.code === '204.02') {
                    return await handleRealTimeResponse();
                }

                if (appConfig.correlationId === appConfig.statusCodeByGuid?.guid && appConfig.statusCodeByGuid?.warnings?.[0]?.code === '204.03') {
                    return await handleNoAvailabilityResponse();
                }

                handleSearchFromScratch(1, appConfig.correlationId, false, false, searchCoordinates);
            };

            runRealTimeSearch();
        }
    }, [appConfig.responseType]);

    useEffect(() => {
        if (!isFirstRun.current && searchCoordinates && searchfilters?.departureFrom?.length && triggerNewSearch) {
            searchTypeRef.current = "Map";
            handleSearchFromScratch(1, '', false, true, searchCoordinates);
        }
    }, [triggerNewSearch, searchCoordinates, searchfilters?.departureFrom?.length]);

    useEffect(() => {
        if (performPriceExpandedSearch) {
            searchTypeRef.current = "Popup";
            handleSearchFromScratch(1, appConfig.correlationId, false, false, searchCoordinates);
            setPerformPriceExpandedSearch(false);
        }
    }, [performPriceExpandedSearch, searchfilters]);

    const handleSearchFromScratch = (
        page = 1,
        urlGUID = "",
        initialSearch = true,
        bigSearch = false,
        searchCoordinates = ''
    ) => {
        try {

            if (page === 1 && urlGUID === '' && searchCoordinates === '') {
                // Only reachable from the archived sidebar's Search button (./SidebarFilters).
                if (!searchfilters.selectedDesireCountry) {
                    return toast.info(T("Toasts:Please_Choose_Package_Destination"));
                }

                if (!searchfilters?.departureFrom?.length) {
                    return toast.info(T("Toasts:Please_Choose_Departure"));
                }

                if (
                    !searchfilters.travelDate.startDate ||
                    !searchfilters.travelDate.endDate
                ) {
                    return toast.info(T("Toasts:Please_Select_Date_Range"));
                }
            }

            let localGeniusDestinationObject = null;
            localGeniusDestinationObject = { ...geniusDestinationObject }
            setGeniusDestinationObject(null);
            // It is a search coodinates based map search so shouldn't add in history.
            const coordinatesFound = triggerNewSearch && searchCoordinates;
            const params = getSearchOnlyListingParams({
                guid: urlGUID,
                page: page,
                searchfilters,
                loginconfig: loginConfig,
                initialSearch,
                SearchType: searchTypeRef.current,
                bigSearch: bigSearch,
                searchCoordinates: triggerNewSearch ? searchCoordinates : '',
            });
            if (appConfig.responseType === "RealTime") {
                // A RealTime retry is a continuation of the in-flight search, not a
                // new one. searchCoordinates is already cleared here, so we must NOT
                // recompute the coordinates flag — preserve it so the second phase
                // still skips the dense-cluster animation. Only clear trigger state.
                setTriggerNewSearch(false);
                setSearchCoordinates('');
            } else {
                // New user-initiated search: (re)set the coordinates flag definitively
                // so the animation effect knows whether to skip the dense-cluster zoom.
                isCoordinatesSearchRef.current = Boolean(coordinatesFound);
            }
            dispatch(getHotels({ params, T, dispatch, navigate, shouldAddToSearchHistory: coordinatesFound ? false : true, geniusDestinationObject: localGeniusDestinationObject }));
        } catch (error) {
            console.error('MapOffers: Error in handleSearchFromScratch', error);
            dispatch(setActivityIndicator(false));
        }
    };

    function fitBoundsToMarkers(map, maps, locations) {
        // const windowWidth = window.innerWidth;
        if (!locations || locations.length === 0) return;

        const bounds = new maps.LatLngBounds();

        locations.forEach((loc) => {
            bounds.extend(new maps.LatLng(Number(loc.lat), Number(loc.lng)));
        });

        // Treat fitting all markers as programmatic movement so the idle listener
        // does not immediately reverse-geocode the map center and refill cityArea.
        const movementSeq = programmaticMovementSeqRef.current + 1;
        programmaticMovementSeqRef.current = movementSeq;
        isAnimatingRef.current = true;
        resetMapAreaState(true);
        map.fitBounds(bounds, getMapBoundsPadding());
        const clearProgrammaticMovement = () => {
            if (movementSeq === programmaticMovementSeqRef.current) {
                isAnimatingRef.current = false;
            }
        };
        maps.event.addListenerOnce(map, "idle", () => {
            clearProgrammaticMovement();
        });
        setTimeout(clearProgrammaticMovement, 1200);
    }

    function getBoundsForLocations(maps, locations) {
        if (!locations || locations.length === 0) return null;

        const bounds = new maps.LatLngBounds();
        locations.forEach((loc) => {
            bounds.extend(new maps.LatLng(Number(loc.lat), Number(loc.lng)));
        });

        return bounds;
    }

    function wait(duration) {
        return new Promise((resolve) => setTimeout(resolve, duration));
    }

    function resetMapAreaState(shouldClearCoordinates = false) {
        geocodeRequestSeqRef.current += 1;
        lastMapCenterRef.current = { lat: null, lng: null };
        lastCityAreaRef.current = "";

        if (mapChangeTimerRef.current) {
            clearTimeout(mapChangeTimerRef.current);
            mapChangeTimerRef.current = null;
        }

        setCityArea('');
        if (shouldClearCoordinates) {
            setSearchCoordinates('');
        }
    }

    function getMapBoundsPadding() {
        return { top: 90, right: 10, bottom: 270, left: 10 };
    }

    async function animateCameraInLimitedSteps(map, targetCenter, targetZoom, duration = 1800, steps = 5, isCancelled = () => false) {
        const startCenter = map.getCenter();
        const startZoom = Number(map.getZoom());

        if (!startCenter || !targetCenter || !Number.isFinite(startZoom) || !Number.isFinite(targetZoom)) {
            return;
        }

        const start = {
            lat: startCenter.lat(),
            lng: startCenter.lng(),
            zoom: startZoom,
        };
        const end = {
            lat: targetCenter.lat(),
            lng: targetCenter.lng(),
            zoom: targetZoom,
        };
        const lngDelta = ((((end.lng - start.lng) % 360) + 540) % 360) - 180;
        const easeInOutCubic = (progress) => (
            progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2
        );

        if (map.setOptions) {
            map.setOptions({ isFractionalZoomEnabled: true });
        }

        for (let step = 1; step <= steps; step += 1) {
            if (isCancelled()) return;
            const progress = step / steps;
            const eased = easeInOutCubic(progress);
            const center = {
                lat: start.lat + (end.lat - start.lat) * eased,
                lng: start.lng + lngDelta * eased,
            };
            const zoom = start.zoom + (end.zoom - start.zoom) * eased;

            if (map.moveCamera) {
                map.moveCamera({ center, zoom });
            } else {
                map.setCenter(center);
                map.setZoom(zoom);
            }

            await wait(duration / steps);
        }
    }

    async function animateSlowZoomOutToAllMarkers(map, maps, locations) {
        const bounds = getBoundsForLocations(maps, locations);
        if (!bounds) return;

        const padding = getMapBoundsPadding();
        const startingZoom = Number(map.getZoom());
        const startingCenter = map.getCenter();

        map.fitBounds(bounds, padding);

        await new Promise((resolve) => {
            maps.event.addListenerOnce(map, "idle", resolve);
        });

        const targetZoom = Number(map.getZoom());
        const targetCenter = map.getCenter();

        if (!Number.isFinite(startingZoom) || !Number.isFinite(targetZoom)) {
            fitBoundsToMarkers(map, maps, locations);
            return;
        }

        if (startingCenter && map.moveCamera) {
            map.moveCamera({ center: startingCenter, zoom: startingZoom });
        } else {
            if (startingCenter) {
                map.setCenter(startingCenter);
            }
            map.setZoom(startingZoom);
        }

        await wait(120);

        // Smooth enough to feel animated, but capped to a few camera updates so
        // Google Maps does not spam viewport requests during the transition.
        await animateCameraInLimitedSteps(map, targetCenter, targetZoom, 1800, 15);
    }

    function getDistanceInMeters(pointA, pointB) {
        const toRadians = (value) => (value * Math.PI) / 180;
        const earthRadiusInMeters = 6371000;

        const latitudeA = Number(pointA.lat);
        const longitudeA = Number(pointA.lng);
        const latitudeB = Number(pointB.lat);
        const longitudeB = Number(pointB.lng);

        const deltaLatitude = toRadians(latitudeB - latitudeA);
        const deltaLongitude = toRadians(longitudeB - longitudeA);

        const a =
            Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
            Math.cos(toRadians(latitudeA)) *
            Math.cos(toRadians(latitudeB)) *
            Math.sin(deltaLongitude / 2) *
            Math.sin(deltaLongitude / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return earthRadiusInMeters * c;
    }

    function findDenseClusters(locations, radiusInMeters = 5000, minClusterSize = 3) {
        if (!locations || locations.length < minClusterSize) {
            return [];
        }

        return locations
            .map((centerLocation) => locations.filter((candidateLocation) =>
                getDistanceInMeters(centerLocation, candidateLocation) <= radiusInMeters
            ))
            .filter((cluster) => cluster.length >= minClusterSize);
    }

    function getDensestCluster(clusters) {
        if (!clusters || clusters.length === 0) return null;

        // Find cluster with most markers
        return clusters.reduce((densest, current) =>
            current.length > densest.length ? current : densest
        );
    }

    function fitBoundsToCluster(map, maps, cluster) {
        if (!cluster || cluster.length === 0) return;

        const bounds = new maps.LatLngBounds();
        cluster.forEach((loc) => {
            bounds.extend(new maps.LatLng(Number(loc.lat), Number(loc.lng)));
        });

        map.fitBounds(bounds, getMapBoundsPadding());
    }

    async function animateClusterZoom(map, maps, locations) {
        if (
            !locations ||
            locations.length < 3 ||
            isCoordinatesSearchRef.current ||
            searchfilters?.selectedDesireCountry?.id !== "00001" ||
            searchfilters?.selectedDesireCountry?.airportCode !== "001"
        ) {
            // Skip the dense-cluster zoom for coordinates-based searches or when
            // there are too few locations; just fit all bounds instead.
            fitBoundsToMarkers(map, maps, locations);
            return;
        }

        // Find clusters
        const clusters = findDenseClusters(locations, 5000, 2);

        // If no dense clusters found, just fit all bounds
        if (clusters.length === 0) {
            fitBoundsToMarkers(map, maps, locations);
            return;
        }

        // Get the densest cluster
        const densestCluster = getDensestCluster(clusters);

        // Only animate if cluster has reasonable size difference
        if (densestCluster.length < 2) {
            fitBoundsToMarkers(map, maps, locations);
            return;
        }

        // Step 1: Zoom into the densest cluster
        // Set flag before any programmatic map movement so event listeners
        // don't treat animation steps as user interactions or trigger geocoder calls.
        programmaticMovementSeqRef.current += 1;
        isAnimatingRef.current = true;
        try {
            fitBoundsToCluster(map, maps, densestCluster);

            // Step 2: Wait a bit and then slowly zoom out to show all locations
            await wait(4000); // pause on cluster before zooming out
            await animateSlowZoomOutToAllMarkers(map, maps, locations);
            hideGreenButtonRef.current = true;
        } finally {
            isAnimatingRef.current = false;
            if (searchTypeRef.current === "Filter" && searchfilters?.selectedDesireCountry?.airportCode === "001") {
                resetMapAreaState(true);
            }
        }
    }

    const handleOnOfferCardClick = useCallback((offer, locationId) => {
        // An "Anywhere" search skips both the detail page and the desktop sidebar.
        const expediaUrl = getAnywhereSearchExpediaUrl(offer, searchfilters);
        if (expediaUrl) {
            openExternalLink(expediaUrl);
            return;
        }

        if (isMobile || isTablet) {
            !offer?.detailsLink &&
                navigateToDetailPage(
                    offer,
                    dispatch,
                    loginConfig,
                    searchfilters,
                    navigate,
                    "hotel",
                    true,
                    false,
                    false,
                    null, // Branch data null
                )
            return;
        }

        setSwitchToDetailSideBar(true);
        setSelectedOffer(offer);

        if (locationId) {
            const offerIndex = appConfig.offers.findIndex(f => f?.id === locationId);
            if (offerIndex >= 0) {
                scrollToOffer(offerIndex);
            }
            if (slider && offerIndex >= 0) {
                slider?.current?.slickGoTo(offerIndex, true);
            }
        }
    }, [
        appConfig.offers,
        dispatch,
        isMobile,
        loginConfig,
        navigate,
        searchfilters,
        slider,
    ]);

    const handleMarkerClick = useCallback((locationId) => {
        const _offer = appConfig.offers?.find((f) => f?.id === locationId);
        if (_offer) {
            handleOnOfferCardClick(_offer, locationId);
        }
    }, [appConfig.offers, handleOnOfferCardClick]);

    // Swiping the mobile bottom slider selects that offer and flies the map to its
    // marker, zooming in far enough to pull it out of any overlapping cluster.
    // Slide 0 is the "previous locations" box when visited > 0, and the trailing
    // "further locations" box has no offer, so both are ignored.
    const handleSliderAfterChange = async (currentSlide) => {
        const offerIndex = currentSlide - (visited > 0 ? 1 : 0);
        const offer = appConfig.offers?.[offerIndex];
        if (!offer) return;

        setSelectedOffer(offer);

        const lat = Number(offer?.location?.geoLocation?.latitude);
        const lng = Number(offer?.location?.geoLocation?.longitude);
        if (!mapInstance || !mapsApi || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

        // Programmatic move: keep the idle listener from reverse-geocoding the new
        // center and surfacing the "find in area" button. A newer swipe bumps the
        // sequence, which cancels this animation mid-flight.
        const movementSeq = programmaticMovementSeqRef.current + 1;
        programmaticMovementSeqRef.current = movementSeq;
        isAnimatingRef.current = true;
        // Never zoom out if the user is already closer than the target.
        const targetZoom = Math.max(Number(mapInstance.getZoom()) || 0, SLIDER_MARKER_ZOOM);
        try {
            await animateCameraInLimitedSteps(
                mapInstance,
                new mapsApi.LatLng(lat, lng),
                targetZoom,
                600,
                12,
                () => movementSeq !== programmaticMovementSeqRef.current
            );
        } finally {
            if (movementSeq === programmaticMovementSeqRef.current) {
                isAnimatingRef.current = false;
            }
        }
    };

    // Tapping a stack badge zooms in until its pins separate. Left as a user
    // zoom (not programmatic), same as pinching in by hand.
    stackBadgeClickRef.current = (positions) => {
        if (!mapInstance || !mapsApi || !positions?.length) return;
        const maxZoom = Math.max(Number(mapInstance.getZoom()) || 0, MARKER_STACK_MAX_ZOOM);
        const bounds = new mapsApi.LatLngBounds();
        positions.forEach((position) => bounds.extend(position));
        mapInstance.fitBounds(bounds, getMapBoundsPadding());
        mapsApi.event.addListenerOnce(mapInstance, "idle", () => {
            if (mapInstance.getZoom() > maxZoom) mapInstance.setZoom(maxZoom);
        });
    };

    // Every overlay draw can change which pins overlap, so coalesce them into
    // one stack-badge pass per frame.
    const scheduleStackBadgeUpdate = () => {
        if (stackBadgeFrameRef.current) return;
        stackBadgeFrameRef.current = requestAnimationFrame(() => {
            stackBadgeFrameRef.current = null;
            applyMarkerStackBadges(overlaysRef.current, (positions) => stackBadgeClickRef.current?.(positions));
        });
    };

    useEffect(() => {
        if (!mapInstance || !mapsApi) return;

        const buildMarkerElement = (loc, isSelected, labelText) => {
            const container = document.createElement("div");
            container.className = "map-offers-marker";
            container.style.cursor = "pointer";

            if (isSelected) {
                // Overlays stack in creation order, so lift the selected pin above
                // any neighbouring price pins that would otherwise cover it.
                container.style.zIndex = "1";
                const img = document.createElement("img");
                img.src = HoverPin;
                img.alt = "marker-green-icon";
                container.appendChild(img);
                return container;
            }

            const img = document.createElement("img");
            img.src = PricePin;
            img.alt = "marker-green-icon";
            container.appendChild(img);

            const price = document.createElement("div");
            price.className = "price-marker";
            price.textContent = labelText;
            container.appendChild(price);

            const hoverImg = document.createElement("img");
            hoverImg.className = "marker-hover-img";
            hoverImg.src = HoverPin;
            hoverImg.alt = "marker-green-icon";
            container.appendChild(hoverImg);

            return container;
        };

        const createOverlay = (loc, isSelected, labelText) => {
            const overlay = new mapsApi.OverlayView();
            overlay.position = new mapsApi.LatLng(Number(loc.lat), Number(loc.lng));
            overlay.div = null;
            overlay.onAdd = () => {
                overlay.div = buildMarkerElement(loc, isSelected, labelText);
                overlay.div.addEventListener("click", () => handleMarkerClick(loc.id));
                const panes = overlay.getPanes();
                panes.overlayMouseTarget.appendChild(overlay.div);
            };
            overlay.draw = () => {
                if (!overlay.div) return;
                const projection = overlay.getProjection();
                if (!projection) return;
                const point = projection.fromLatLngToDivPixel(overlay.position);
                if (!point) return;
                overlay.point = point;
                overlay.div.style.position = "absolute";
                overlay.div.style.left = `${point.x}px`;
                overlay.div.style.top = `${point.y}px`;
                overlay.div.style.transform = "translate(-50%, -100%)";
                scheduleStackBadgeUpdate();
            };
            overlay.onRemove = () => {
                if (overlay.div && overlay.div.parentNode) {
                    overlay.div.parentNode.removeChild(overlay.div);
                }
                overlay.div = null;
                scheduleStackBadgeUpdate();
            };
            overlay.setMap(mapInstance);
            return overlay;
        };

        const updateOverlay = (overlay, loc, isSelected, labelText) => {
            overlay.position = new mapsApi.LatLng(Number(loc.lat), Number(loc.lng));
            if (overlay.div) {
                const nextDiv = buildMarkerElement(loc, isSelected, labelText);
                nextDiv.addEventListener("click", () => handleMarkerClick(loc.id));
                overlay.div.replaceWith(nextDiv);
                overlay.div = nextDiv;
            }
            if (overlay.draw) {
                overlay.draw();
            }
        };

        const nextIds = new Set(hotelLocations.map((loc) => loc.id));

        overlaysRef.current.forEach((overlay, id) => {
            if (!nextIds.has(id)) {
                overlay.setMap(null);
                overlaysRef.current.delete(id);
            }
        });

        hotelLocations.forEach((loc) => {
            if (!loc?.lat || !loc?.lng || !loc?.id) return;

            const perPersonPrice = loc?.perPersonPrice;
            if (!perPersonPrice?.currency || perPersonPrice?.totalPrice === null || perPersonPrice?.totalPrice === undefined) {
                return;
            }

            const isSelected = selectedOffer?.id === loc.id;
            const labelText = `${getCurrencyBadge(perPersonPrice.currency)}${Number(perPersonPrice.totalPrice).toFixed(0)}`;

            const existingOverlay = overlaysRef.current.get(loc.id);
            if (existingOverlay) {
                updateOverlay(existingOverlay, loc, isSelected, labelText);
            } else {
                const overlay = createOverlay(loc, isSelected, labelText);
                overlaysRef.current.set(loc.id, overlay);
            }
        });
    }, [
        handleMarkerClick,
        hotelLocations,
        mapInstance,
        mapsApi,
        selectedOffer,
    ]);

    useEffect(() => {
        return () => {
            overlaysRef.current.forEach((overlay) => overlay.setMap(null));
            overlaysRef.current.clear();
            cancelAnimationFrame(stackBadgeFrameRef.current);
        };
    }, []);

    const scrollToOffer = (index) => {
        offerRefs.current[index]?.scrollIntoView({
            behavior: "smooth",
            inline: "center" // aligns horizontally
        });
    };

    // Mobile responsiveness
    useEffect(() => {
        width < 767 ? setIsMobile(true) : setIsMobile(false);
    }, [width]);

    function SampleNextArrow({ onClick }) {
        return (
            slider?.current?.innerSlider?.state?.currentSlide <
            slider?.current?.innerSlider?.state?.slideCount -
            slider?.current?.innerSlider?.props?.slidesToShow && (
                // <span
                //     className="fas fa-long-arrow-alt-right slick-arrow arrow-right"
                //     onClick={onClick}
                // ></span>
                <FontAwesomeIcon icon={faArrowRightLong} className="slick-arrow arrow-right" onClick={onClick} />
            )
        );
    }

    function SamplePrevArrow({ onClick }) {
        return (
            slider?.current?.innerSlider?.state?.currentSlide > 0 && (
                // <span
                //     className="fas fa-long-arrow-alt-left slick-arrow arrow-left"
                //     onClick={onClick}
                // ></span>
                <FontAwesomeIcon icon={faArrowLeftLong} className="slick-arrow arrow-left" onClick={onClick} />
            )
        );
    }

    SampleNextArrow.propTypes = {
        onClick: PropTypes.func,
    };

    SamplePrevArrow.propTypes = {
        onClick: PropTypes.func,
    };

    const settings = {
        slidesToShow: 1,
        infinite: false,
        speed: 300,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        arrows: true,
        afterChange: handleSliderAfterChange,
        //    centerMode: true,
        //    variableWidth: true,
        responsive: [
            {
                breakpoint: 991,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1,
                },
            },
            {
                breakpoint: 576,
                settings: {
                    slidesToShow: 1,
                    centerPadding: "0",
                },
            },
        ],
    };

    const handleMapChange = ({ center }) => {
        if (!mapsApi || !center) return;


        const last = lastMapCenterRef.current;
        const latChanged = last.lat === null || Math.abs(center.lat - last.lat) > 0.0005;
        const lngChanged = last.lng === null || Math.abs(center.lng - last.lng) > 0.0005;

        if (!latChanged && !lngChanged) return;

        lastMapCenterRef.current = { lat: center.lat, lng: center.lng };

        if (mapChangeTimerRef.current) {
            clearTimeout(mapChangeTimerRef.current);
        }

        const requestSeq = geocodeRequestSeqRef.current + 1;
        geocodeRequestSeqRef.current = requestSeq;

        mapChangeTimerRef.current = setTimeout(() => {
            if (requestSeq !== geocodeRequestSeqRef.current || isAnimatingRef.current) {
                return;
            }

            const geocoder = new mapsApi.Geocoder();
            const latlng = { lat: center.lat, lng: center.lng };

            geocoder.geocode({ location: latlng }, (results, status) => {
                if (requestSeq !== geocodeRequestSeqRef.current || isAnimatingRef.current) {
                    return;
                }

                const currentCenter = mapInstance?.getCenter?.();
                if (currentCenter) {
                    const centerMoved =
                        Math.abs(currentCenter.lat() - center.lat) > 0.0005 ||
                        Math.abs(currentCenter.lng() - center.lng) > 0.0005;

                    if (centerMoved) {
                        return;
                    }
                }

                if (status === "OK" && results[0]) {
                    const location = results[0].geometry.location;
                    const closestLat = location.lat();
                    const closestLng = location.lng();
                    const nextSearchCoordinates = `|${closestLat},${closestLng}|`;

                    if (nextSearchCoordinates !== searchCoordinates) {
                        setSearchCoordinates(nextSearchCoordinates);
                    }

                    // Find the most relevant city/area name
                    const addressComponents = results[0].address_components;
                    const cityComponent =
                        addressComponents.find((c) => c.types.includes("locality")) ||
                        addressComponents.find((c) => c.types.includes("administrative_area_level_2")) ||
                        addressComponents.find((c) => c.types.includes("administrative_area_level_1"));

                    if (cityComponent) {
                        const nextCity = cityComponent.long_name;
                        // if (searchTypeRef.current === "Filter" && searchfilters?.selectedDesireCountry?.airportCode === "001") return;

                        if (nextCity !== lastCityAreaRef.current && hideGreenButtonRef.current === false) {
                            lastCityAreaRef.current = nextCity;
                            setCityArea(nextCity);
                        } else {
                            hideGreenButtonRef.current = false; // reset the anywhere search ref after the first map move to allow future updates to city area when user moves the map again   
                        }
                    }
                } else {
                    console.error("Geocoder failed:", status);
                }
            });
        }, 300);
    };

    const handleSearchAreaClick = async () => {
        try {
            // dispatch(setActivityIndicator(true));
            // const params = {
            //     searchTerm: cityArea,
            //     Locale: loginConfig?.locale?.code,
            // };
            // const response = await SearchCountries(params);
            // const locations = response?.locations;

            // const result = getBestMatch(params.searchTerm, locations);
            // console.log('Matched location:', result);
            // setGeniusDestinationObject(result);
            // dispatch(setActivityIndicator(false));

            setTimeout(() => {
                setTriggerNewSearch(true);
            }, 500);

        } catch (error) {
            console.error(error, 'in search area get offers');
            // toast.error(T('Toasts:Toast_Description_Server_Error_Message'));
            // dispatch(setActivityIndicator(false));
        }
    };

    const handleArrowClick = (direction) => {
        const scrollContainer = scrollRef.current;

        if (!scrollContainer) return;

        const scrollAmount = 350; // adjust for how far each click should scroll

        if (direction === "left") {
            scrollContainer.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else if (direction === "right") {
            scrollContainer.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    return (
        <div className="main-wrapper flight-page map-page">
            <main className="body-wrapper">
                <AppHeader offersMap={true} />
                <div className="map-page-wrapper">
                    <div className="map-wrapper">
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: AppConfig.googleMapAPIKey }}
                            defaultCenter={defaultProps.center}
                            defaultZoom={defaultProps.zoom}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => {
                                setMapInstance(map);
                                setMapsApi(maps);
                                // Initial load handled by useEffect with proper condition checks
                            }}
                            options={{
                                fullscreenControl: false,
                            }}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        >
                        </GoogleMapReact>
                        <div className="bottom-offers-container hide-on-small-screen">
                            {false && cityArea &&
                                <div className={'city-area-txt'} onClick={handleSearchAreaClick}>
                                    <span>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {/* Find in {cityArea} */}
                                        {" "}{T("Buttons:Find_In_Area")}
                                    </span>
                                </div>}
                            {!switchToDetailSideBar ?
                                <img className="genius-icon" src={GeniusIcon} /> : null}
                            {appConfig.offers?.length ? <div className="bottom-offers" ref={bottomOffersRef}>
                                <div className="mapoffers-arrow left" onClick={() => handleArrowClick('left')}>
                                    <FontAwesomeIcon icon={faAngleLeft} />
                                </div>
                                {/* <HorizontalScroll
                                    reverseScroll={true} // set to true if you want natural trackpad scroll
                                    config={{stiffness: 160, damping: 20}} // smoothness
                                    className={!switchToDetailSideBar && !showFilters ? "" : "sidebarshown"}
                                > */}
                                <div ref={scrollRef} className="bottom-offers-inner">
                                    {visited > 0 && (
                                        <div
                                            className="revisit-box"
                                            onClick={() => {
                                                searchTypeRef.current = "Pagination";
                                                setPage(page - 1);
                                                navigate("/map/" + appConfig.correlationId + `/offers/${prevRoute}/${page - 1}`, {
                                                    replace: true,
                                                });
                                            }}
                                        >
                                            {/* <div className="revisit-txt">{T('Filters:Map_Offer_Revisit')}</div> */}
                                            <div className="revisit-no">{visited}</div>
                                            <div
                                                className="p-locations">{T('Filters:Map_Offer_Previous_Locations')}</div>
                                        </div>
                                    )}
                                    {appConfig.offers?.map((offer, index) => {
                                        return (
                                            <div
                                                key={index}
                                                ref={(el) => (offerRefs.current[index] = el)}
                                                onClick={() => handleOnOfferCardClick(offer, null)}
                                            >
                                                <HotelItemMap
                                                    offer={offer}
                                                    isDesktop={false}
                                                    isMobile={false}
                                                    isSelected={selectedOffer?.id === offer?.id}
                                                />
                                            </div>
                                        );
                                    })}
                                    {remaining > 0 && (
                                        <div
                                            className="revisit-box green-box"
                                            onClick={() => {
                                                searchTypeRef.current = "Pagination";
                                                setPage(page + 1);
                                                navigate("/map/" + appConfig.correlationId + `/offers/${prevRoute}/${page + 1}`, {
                                                    replace: true,
                                                });
                                            }}
                                        >
                                            {/* <div className="revisit-txt">{T('Filters:Map_Offer_Explore')}</div> */}
                                            <div className="revisit-no">{remaining}</div>
                                            <div
                                                className="p-locations">{T('Filters:Map_Offer_Further_Locations')}</div>
                                        </div>
                                    )}
                                </div>
                                {/* </HorizontalScroll> */}
                                <div className="mapoffers-arrow right" onClick={() => handleArrowClick('right')}>
                                    <FontAwesomeIcon icon={faAngleRight} />
                                </div>
                            </div> : null}
                        </div>

                        <div className="mobile-only bottom-offers-container">
                            {false && cityArea &&
                                <div className={'city-area-txt'} onClick={handleSearchAreaClick}>
                                    <span>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {/* Find in {cityArea} */}
                                        {" "}{T("Buttons:Find_In_Area")}
                                    </span>
                                </div>}
                            {appConfig.offers?.length ? (
                                <div className="flight-slider bottom-offers">
                                    <Slider
                                        ref={slider}
                                        key={offersKey}
                                        {...settings}
                                        initialSlide={visited > 0 ? 1 : 0}
                                    >
                                        {visited > 0 && (
                                            <div
                                                className="revisit-box"
                                                onClick={() => {
                                                    searchTypeRef.current = "Pagination";
                                                    setPage(page - 1);
                                                    navigate("/map/" + appConfig.correlationId + `/offers/${prevRoute}/${page - 1}`, {
                                                        replace: true,
                                                    });
                                                }}
                                            >
                                                {/* <div className="revisit-txt">{T('Filters:Map_Offer_Revisit')}</div> */}
                                                <div className="revisit-no">{visited}</div>
                                                <div
                                                    className="p-locations">{T('Filters:Map_Offer_Previous_Locations')}</div>
                                            </div>
                                        )}
                                        {appConfig.offers?.map((offer, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => handleOnOfferCardClick(offer)}
                                                >
                                                    <HotelItemMap
                                                        offer={offer}
                                                        isDesktop={false}
                                                        isMobile={false}
                                                        isSelected={selectedOffer?.id === offer?.id}
                                                    />
                                                </div>
                                            );
                                        })}
                                        {remaining > 0 && (
                                            <div
                                                className="revisit-box green-box"
                                                onClick={() => {
                                                    searchTypeRef.current = "Pagination";
                                                    setPage(page + 1);
                                                    navigate("/map/" + appConfig.correlationId + `/offers/${prevRoute}/${page + 1}`, {
                                                        replace: true,
                                                    });
                                                }}
                                            >
                                                {/* <div className="revisit-txt">{T('Filters:Map_Offer_Explore')}</div> */}
                                                <div className="revisit-no">{remaining}</div>
                                                <div
                                                    className="p-locations">{T('Filters:Map_Offer_Further_Locations')}</div>
                                            </div>
                                        )}
                                    </Slider>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="filter-mob-wrapper">
                        <div className="filter-toggles back-btn">
                            {prevRoute === 'chat' ? (
                                <a className="animated-back-btn" onClick={() => navigate(-1)}>
                                    <Lottie animationData={GeniusJson} loop={true} className='genius-animation' />
                                    <Lottie animationData={BulbJson} loop={true} className='bulb-animation' />
                                    <span>{T("Buttons:Button_Chat_Ai")}</span>
                                </a>
                            ) :
                                <a onClick={() => {
                                    navigate(
                                        "/search/" +
                                        guid +
                                        "/query?page=1&locale=" +
                                        loginConfig.locale.code +
                                        "&currency=" +
                                        loginConfig?.currency?.code, { replace: true }
                                    );
                                }}>{"< " + T("Buttons:Back")}</a>
                            }
                        </div>
                        {/* The filter panel and its toggle are archived in ./SidebarFilters. */}
                    </div>

                    {switchToDetailSideBar && (
                        <div className="hotel-details-sidebar">
                            <HotelItemDetailMap
                                offer={selectedOffer}
                                index={appConfig.offers.findIndex(f => f?.id === selectedOffer?.id)}
                                handlerOnBack={() => setSwitchToDetailSideBar(false)}
                            />
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default MapOffersPage;
